import type { FC, HTMLAttributes, ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'

export type RadioProps = {
  name: string
  value: string
  label: string | ReactNode
  hasError?: boolean
  selectedValue: string | null
  idx: number
  containerClassName?: string
} & HTMLAttributes<HTMLInputElement>

export const Radio: FC<RadioProps> = ({
  name,
  value,
  label,
  hasError = false,
  selectedValue,
  idx,
  containerClassName,
  ...rest
}) => {
  const {
    register,
    clearErrors,
    formState: { errors },
  } = useFormContext()

  // Tailwind can't handle dynamic class names, so one workaround is adding a comment with the possible classes
  // Source: https://stackoverflow.com/questions/69687530/dynamically-build-classnames-in-tailwindcss
  /* all supported classes for :focus-within z-index props 
    focus-within:z-10 focus-within:z-20 focus-within:z-30 focus-within:z-40
    focus-within:z-50 focus-within:z-60 focus-within:z-70 focus-within:z-80
    */
  return (
    <label
      className={`text-ink-600 border-ink-400 flex flex-row items-center cursor-pointer p-4 first:rounded-t-md first:mt-0 -mt-[2px] last:rounded-b-md focus-within:z-${
        (idx + 1) * 10
      } ${
        hasError
          ? 'border-b-2 last:border-0 focus-within:border-red-600 focus-within:outline-red-600'
          : 'border-2 focus-within:border-blue-600 focus-within:outline-blue-600'
      }${containerClassName ? ` ${containerClassName}` : ''}`}
    >
      <input
        {...register(name, {
          onChange: () => {
            if (errors[name]) {
              clearErrors(name)
            }
          },
        })}
        {...rest}
        type="radio"
        value={value}
        className="mr-4"
        tabIndex={selectedValue === value || !selectedValue ? 0 : -1}
        defaultChecked={selectedValue === value}
      />
      <span>{label}</span>
    </label>
  )
}
